import type { MapEditorCatalogElement } from './mapEditorCatalog';

type SendMessageToUnreal = (message: unknown) => void;

// Rooms carry their resolved geometry, not just an id.
//
// The catalog is a loose JSON file rather than cooked content, so a packaged server has no
// copy to read: it resolved every id to the default gallery and published a straight run of
// identical boxes while the author's own preview — resolved client-side, where the file does
// exist — was correct. Sending the resolution removes the server's dependency on the file.
//
// `id` is still sent and still leads: a server that CAN resolve the catalog keeps doing so,
// and these fields only fill in behind it (UE reads each one only when present). That also
// keeps the payload backward compatible with senders that predate this.
export interface MapLayoutRoom {
  source: 'kit';
  id: string;
  type?: string;
  size?: string;
  // Empty string is meaningful, not missing: it is how the catalog marks an element with no
  // room built yet, and UE clears the reference rather than ignoring the field.
  geometryLevel?: string;
  geometryClass?: string;
}

export interface SpawnLayoutPayload {
  action: 'SPAWN_LAYOUT';
  version: number;
  clearExisting: boolean;
  rooms: MapLayoutRoom[];
  // Build in the requesting client's world only: nothing replicates, the player is not moved, and no
  // CURRENT_GEOMETRY_LIST is reported. Omitted rather than sent as false for a publish, so the payload
  // stays byte-identical to what backends sent before the flag existed — UE defaults it to false.
  preview?: true;
  // Whether this message leaves a draft on the author's screen. UE hides the published layout while a
  // draft is up, so the author judges what Save will produce rather than their draft stacked on top of
  // what everyone else is standing in.
  //
  // Only ever sent as false, and only on a preview, because it exists to split the one case `preview`
  // cannot express on its own: an empty preview spawn is both "I cleared the list" (a draft of nothing —
  // the published rooms stay hidden, or the author cannot see that saving empties the world) and "I am
  // leaving the editor" (the draft is gone — the published rooms must come back). Everywhere else the
  // value equals `preview`, which is UE's default when the field is absent, so those payloads stay
  // byte-identical to what senders predating this field produced.
  draftActive?: false;
}

export const SPAWN_LAYOUT_VERSION = 1;

// An id the catalog does not know still goes on the wire bare. Dropping it would silently
// shorten the layout, and UE already handles an unresolvable room by greyboxing it — a visible
// wrong room an author can see and remove beats a missing one they cannot.
const describeRoom = (
  id: string,
  elements: MapEditorCatalogElement[],
): MapLayoutRoom => {
  const element = elements.find((candidate) => candidate.id === id);
  if (!element) {
    return { source: 'kit', id };
  }

  return {
    source: 'kit',
    id,
    type: element.type,
    size: element.size,
    geometryLevel: element.geometryLevel,
    geometryClass: element.geometryClass,
  };
};

export const buildSpawnLayoutPayload = (
  roomIds: string[],
  preview = false,
  elements: MapEditorCatalogElement[] = [],
  draftActive = preview,
): SpawnLayoutPayload => ({
  action: 'SPAWN_LAYOUT',
  version: SPAWN_LAYOUT_VERSION,
  clearExisting: true,
  rooms: roomIds.map((id) => describeRoom(id, elements)),
  ...(preview ? { preview: true as const } : {}),
  // Emitted only where it disagrees with the default UE would infer, so every payload that was correct
  // before this field existed still goes out unchanged.
  ...(preview && !draftActive ? { draftActive: false as const } : {}),
});

// Deliberately not gated on a non-empty chain. An empty list is still a layout —
// "nothing" — and clearExisting with no rooms is the declarative spelling of a clear,
// so sending it reproduces what the author sees. Refusing to send on empty is what
// broke the prototype's 1:1 guarantee: the button became a no-op while the previously
// built layout stayed standing in UE, so an author who emptied the list watched the
// old rooms remain.
export const spawnLayoutInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  roomIds: string[],
  elements: MapEditorCatalogElement[] = [],
) => {
  sendMessageToUnreal(
    buildSpawnLayoutPayload(roomIds, /*preview=*/ false, elements),
  );
};

// The draft made visible. Sent on every edit so the author sees the floor plan they are assembling —
// UE builds these rooms in their client's world alone, so no other participant is affected and the
// player is not teleported into them.
export const previewLayoutInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  roomIds: string[],
  elements: MapEditorCatalogElement[] = [],
) => {
  sendMessageToUnreal(
    buildSpawnLayoutPayload(roomIds, /*preview=*/ true, elements),
  );
};

export const clearLayoutInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
) => {
  sendMessageToUnreal({ action: 'CLEAR_LAYOUT' });
};

// Tears down the local draft geometry without touching the published layout. Deliberately an empty
// preview spawn rather than a CLEAR_PREVIEW action: clearExisting on a preview already clears the
// preview UE-side, so "a draft of nothing" expresses this exactly, and CLEAR_LAYOUT would be wrong —
// that one is authoritative and would take the published layout down with it.
//
// draftActive:false is the part that makes it a teardown rather than an empty draft. Without it UE read
// this message as "the author cleared their list" and kept the published layout hidden — and because the
// flag lives on the GameInstance, it survived into the next session: a user entering a map with rooms
// already standing saw an empty world until someone published.
export const clearPreviewLayoutInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
) => {
  sendMessageToUnreal(
    buildSpawnLayoutPayload([], /*preview=*/ true, [], /*draftActive=*/ false),
  );
};

// `enabled` is optional on the wire and defaults to true UE-side, but it is always sent
// explicitly here: the sender knows the state it wants, and a toggle would desync if a
// message were retried or dropped.
//
// EDITOR_MODE was previously named PREVIEW_LAYOUT. UE still accepts the old name for
// backends that have not been redeployed, but new senders use EDITOR_MODE — the alias
// is a compatibility shim, not a second supported spelling.
export const setEditorModeInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  enabled: boolean,
) => {
  sendMessageToUnreal({ action: 'EDITOR_MODE', enabled });
};

// UE's editor mode is the overhead plan view: it spawns a camera, takes over the view
// target and blocks player input. That is right for building a floor plan and wrong for
// every other focus — spots, hotspots and AI scripts are all edited by looking at and
// clicking things in-world, which an off-screen pawn with blocked input cannot do.
//
// So it tracks the map-editor focus rather than the editor overlay as a whole. The
// feature gate is mirrored from the Build/Clear buttons: with SPAWN_LAYOUT off there is
// no layout to look at, so seizing the camera would strand the player for nothing.
export const shouldUnrealEditorModeBeOn = (
  activeFocus: string,
  canUseMapEditor: boolean,
): boolean => activeFocus === 'mapEditor' && canUseMapEditor;

// Why the authority would not place a room that was asked for.
//
// 'overlap' is the room that landed on one already standing; 'chain-truncated' is every room after
// it, which was never attempted — UE stops walking the chain at the first refusal rather than
// scattering the remainder around the blockage.
export type MapLayoutRefusalReason = 'overlap' | 'chain-truncated';

export interface MapLayoutRefusal {
  // Position in the chain that was published, not an instance id: a refused room was never spawned
  // and has none. It is what maps a refusal back to the row the author picked.
  index: number;
  id: string;
  reason: MapLayoutRefusalReason;
  // Only an overlap names a culprit. UE sends `blockedBy: { instanceId, id }`; the instance id is
  // dropped here because the web tracks a chain of ids and has nothing to match one against.
  //
  // Carried but not yet shown: the inspector's tooltip names neither room, because the row the
  // marker sits on is already the refused one and the culprit is usually the row above it. Kept
  // because it is part of the message and the next thing to reach for if that turns out too terse.
  blockedById?: string;
}

const REFUSAL_REASONS: MapLayoutRefusalReason[] = [
  'overlap',
  'chain-truncated',
];

/**
 * Reads the `refused` half of a CURRENT_GEOMETRY_LIST payload.
 *
 * Defensive for the same reason the rooms half is: this crosses UE → WebSocket → here, and a
 * malformed entry must cost its own row rather than the whole report. An unknown reason is dropped
 * rather than passed through — every consumer switches on it to decide what to tell the author, and
 * a reason nothing can explain would render as a warning with no text.
 *
 * @param payload - The message's `payload` object, straight off the socket.
 * @returns The refusals in wire order; empty when the field is absent, which is the normal case.
 */
export const parseGeometryRefusals = (
  payload: Record<string, unknown> | null | undefined,
): MapLayoutRefusal[] => {
  const raw = (payload as { refused?: unknown } | null | undefined)?.refused;
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.reduce<MapLayoutRefusal[]>((refusals, entry) => {
    const candidate = entry as Partial<MapLayoutRefusal> & {
      blockedBy?: { id?: unknown };
    };

    if (
      typeof candidate?.index !== 'number' ||
      typeof candidate?.id !== 'string' ||
      !candidate.id ||
      !REFUSAL_REASONS.includes(candidate.reason as MapLayoutRefusalReason)
    ) {
      return refusals;
    }

    const blockedById = candidate.blockedBy?.id;

    refusals.push({
      index: candidate.index,
      id: candidate.id,
      reason: candidate.reason as MapLayoutRefusalReason,
      ...(typeof blockedById === 'string' && blockedById
        ? { blockedById }
        : {}),
    });

    return refusals;
  }, []);
};

/**
 * Finds the refusal that explains a given row of the published chain.
 *
 * Matched on index rather than element id: the same element can appear several times in a chain and
 * only one of those positions may have been refused.
 *
 * @param refusals - Refusals from the last report, or null before one has arrived.
 * @param index - Position in the chain being rendered.
 * @returns The refusal for that position, or undefined when the room was built.
 */
export const findRefusalAt = (
  refusals: MapLayoutRefusal[] | null,
  index: number,
): MapLayoutRefusal | undefined =>
  refusals?.find((refusal) => refusal.index === index);
